import React, { useId } from 'react';

import { Check } from 'lucide-react';

import { cn } from './utils';


/**
 * Boolean checkbox matching the .pen auth design: 18px box, 5px radius,
 * brand-gradient + white check when checked, surface-1 + hairline border when not.
 * Controlled: pass `checked` and an `onChange(event)` handler. Optional `children`
 * render as an inline label to the right of the box.
 */
const Checkbox = React.forwardRef((props, ref) => {
  const {
    className,
    checked = false,
    invalid = false,
    children = null,
    id,
    ...restProps
  } = props;

  const generatedId = useId();
  const inputId = id || generatedId;

  return (
    <label className={cn('flex cursor-pointer select-none items-center gap-2.5', className)} htmlFor={inputId}>
      <input
        ref={ref}
        id={inputId}
        type="checkbox"
        checked={checked}
        className="peer sr-only"
        {...restProps}
      />

      <span
        className={cn(
          'grid size-5 shrink-0 place-items-center rounded-[5px] border transition-colors',
          checked
            ? 'bg-brand-gradient border-transparent'
            : 'bg-surface-1 border-hairline-strong',
          'peer-focus-visible:ring-focus',
          'peer-disabled:opacity-50 peer-disabled:cursor-not-allowed',
          invalid && !checked && 'border-danger',
        )}
      >
        {checked
          ? (
            <Check className="size-[14px] text-white" strokeWidth={3} />
          )
          : null}
      </span>

      {children
        ? (
          <span
            className={cn(
              'text-sm leading-snug text-ink',
              'peer-disabled:cursor-not-allowed peer-disabled:opacity-50',
            )}
          >
            {children}
          </span>
        )
        : null}
    </label>
  );
});

Checkbox.displayName = 'Checkbox';

export { Checkbox };
export default Checkbox;
